import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getProspectPreviousTasks,
  getProspectTaskPDF,
  getProspectTasks,
  getProspectTaskUrl,
  setTaskStarted,
} from "../clients/DPLClient";
import AlertConstants from "../constants/AlertConstants";
import RouteConstants from "../constants/RouteConstants";
import { PdfViewDTO } from "../types/Mobile";
import { ProspectTaskDTO, ProspectTaskResponse } from "../types/Prospect";
import { TaskCategories } from "../types/Tasks";
import { downloadPDF } from "../util/FileUtil";
import { urlReplace } from "../util/RouteUtil";
import { useAuth } from "./AuthContext";
import { useSiteAlerts } from "./SiteAlertContext";

type TaskContextType = {
  maxWeight: number;
  previousTasks: ProspectTaskDTO[];
  toCompleteTasks: ProspectTaskDTO[];
  autoOpenTasks: ProspectTaskDTO[];
  pendingTasks: ProspectTaskDTO[];
  completedTasks: ProspectTaskDTO[];
  taskCount: number;
  completionPercentage: number;
  getTasks: () => Promise<void>;
  navigateToTask: (task: ProspectTaskDTO) => void;
};

const TaskContext = React.createContext<TaskContextType | undefined>(undefined);

const TaskProvider: React.FC = ({ children }) => {
  const { prospectId, asUser } = useAuth();
  const [taskCount, setTaskCount] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [pendingTasks, setPendingTasks] = useState<ProspectTaskDTO[]>([]);
  const [completedTasks, setCompletedTasks] = useState<ProspectTaskDTO[]>([]);
  const [toCompleteTasks, setToCompleteTasks] = useState<ProspectTaskDTO[]>([]);
  const [autoOpenTasks, setAutoOpenTasks] = useState<ProspectTaskDTO[]>([]);
  const [previousTasks, setPreviousTasks] = useState<ProspectTaskDTO[]>([]);
  const [maxWeight, setMaxWeight] = useState(0);
  const [tasksInitialized, setTasksInitialized] = useState(false);
  const hasAutoOpenedRef = useRef(false);

  const history = useHistory();
  const { addAlert } = useSiteAlerts();

  const getTasksData = async (prospectId: number) => {
    const result: ProspectTaskResponse = await getProspectTasks(prospectId);
    if (result) {
      setMaxWeight(result.maxWeight);
      setTaskCount(result.count);
      setCompletionPercentage(result.completionPercentage);
      setPendingTasks(result.pending);
      setCompletedTasks(result.completed);
      setToCompleteTasks(result.toDoTasks);
      setAutoOpenTasks(result.autoOpenTasks);
    }
  };

  const getPreviousTaskData = async (prospectId: number) => {
    const result: ProspectTaskDTO[] = await getProspectPreviousTasks(prospectId);
    setPreviousTasks(result);
  };

  const getTasks = (): Promise<void> => {
    if (prospectId) {
      return Promise.all([getTasksData(prospectId), getPreviousTaskData(prospectId)]).then(() => undefined);
    }
    return Promise.resolve();
  };

  const viewTask = async (task: ProspectTaskDTO) => {
    const { taskId, surveyId } = task;
    // Attempt to load the task's PDF file
    if (window.isNativeApp) {
      // for native app, post pdf detials to listener
      const pdfDetails: PdfViewDTO = {
        type: "pdf",
        uri: getProspectTaskUrl(prospectId!, taskId),
        useAuth: true,
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(pdfDetails));
    } else {
      const response = await getProspectTaskPDF(prospectId!, taskId);
      if (response?.data?.size) {
        downloadPDF(response);
      } else if (!!surveyId) {
        history.push(urlReplace(RouteConstants.SURVEY_READ_ONLY, { surveyId }));
      } else {
        addAlert({
          type: AlertConstants.DANGER,
          text: "File not found!",
        });
      }
    }
  };

  const navigateToTask = async (task: ProspectTaskDTO) => {
    const { taskCategoryId, taskTypeId, taskId, surveyId, completedDate, startedDate } = task;
    let url = "";
    switch (taskCategoryId) {
      case TaskCategories.WAIVER:
        const route = RouteConstants.WAIVER;
        url = urlReplace(route, { waiverId: taskId, taskTypeId: taskTypeId });
        break;
      case TaskCategories.PROFILE:
        url = RouteConstants.PROFILE;
        break;
      case TaskCategories.NAME_PRONUNCIATION:
        url = RouteConstants.NAME_PRONUNCIATION;
        break;
      case TaskCategories.QUESTIONNAIRE:
        url = surveyId
          ? urlReplace(RouteConstants.SURVEY, { surveyId })
          : urlReplace(RouteConstants.QUESTIONNAIRE, { questionnaireId: taskId });
        break;
      case TaskCategories.UPLOAD_TASK:
      default:
        url = RouteConstants.VISION_ASSESSMENT_FORM;
    }
    const isProfile = url.indexOf(RouteConstants.PROFILE) != -1;
    const isNamePronunciation = url.indexOf(RouteConstants.NAME_PRONUNCIATION) != -1;
    const isVisionForm = url === RouteConstants.VISION_ASSESSMENT_FORM;
    if (!completedDate) {
      if (!startedDate) {
        await setTaskStarted(prospectId!, taskId);
      }
      history.push(url);
    } else if (isProfile || isNamePronunciation || isVisionForm) {
      history.push(url);
    } else {
      viewTask(task);
    }
  };

  useEffect(() => {
    if (prospectId) {
      getTasks();
      setTasksInitialized(true);
    }
  }, [prospectId]);

  useEffect(() => {
    // only fire once with the ref
    if (
      tasksInitialized &&
      autoOpenTasks.length > 0 &&
      history &&
      !window.isNativeApp &&
      !hasAutoOpenedRef.current &&
      !asUser // auto open tasks if not acting as prospect
    ) {
      hasAutoOpenedRef.current = true;
      navigateToTask(autoOpenTasks[0]);
    }
  }, [tasksInitialized, autoOpenTasks, history]);

  return (
    <TaskContext.Provider
      value={{
        maxWeight: maxWeight,
        toCompleteTasks,
        previousTasks,
        taskCount,
        completionPercentage,
        pendingTasks,
        completedTasks,
        autoOpenTasks,
        getTasks,
        navigateToTask,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

const useTasks = (): TaskContextType => {
  const context: TaskContextType | undefined = useContext(TaskContext);
  if (context === undefined) {
    throw new Error(`useTasks must be used within a TaskProvider`);
  }
  return context;
};

export { TaskProvider, useTasks };
